<template>
    <div class="list-platser">
        <KHeader heading="Platser">
            <KButton variant="hollow" size="slim" :icon-path="PlusCircleBlue" @click="() => openForm()">
                Skapa ny plats
            </KButton>
        </KHeader>

        <KFilter name="platser-filter" :schema="filterSchema" @submit="onFilter" />

        <KTable
            paginator
            alwaysShowPaginator
            name="platser-table"
            :columns="columns"
            :actions="tableActions"
            :value="rows"
            :loading="isLoading"
            :totalRecords="count"
            :first="first"
            @page="onPage"
        />
    </div>
</template>

<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "ListPlatser",
        tabName: "Platser",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
    import { openDialog } from "@/utils";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import KButton from "@/components/Button.vue";
    import KHeader from "@/components/KHeader.vue";
    import PlusCircleBlue from "@/assets/plus_circle_blue.svg";
    import FormPlats from "@/tabs/Skreg/Platser/FormPlats.vue";
    import { useStore } from "@/store";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";

    const store = useStore();

    const isLoading = ref(false);
    const filterParams = ref<Record<string, any>>({});
    const subsetName = ref("default");
    const page = ref(1);
    const limit = ref(20);
    // Antalet rader som finns för subsetet, som paginatorn räknar sidor på
    const count = computed(() => store.getters["skreg/platser/platsSubsetTotal"]({ subsetName: subsetName.value }));

    const first = computed(() => (page.value - 1) * limit.value);

    const newlyCreated = computed(() =>
        store.getters["skreg/platser/platsSubset"]({ subsetName: "newlyCreated", offset: 0, limit: 0, toArray: false })
    );
    const newlyUpdated = computed(() =>
        store.getters["skreg/platser/platsSubset"]({ subsetName: "newlyUpdated", offset: 0, limit: 0, toArray: false })
    );

    watch([() => newlyCreated.value.size, () => newlyUpdated.value.size], () => {
        fetchList();
    });

    const rows = computed(() =>
        store.getters["skreg/platser/platsSubset"]({
            subsetName: subsetName.value,
            offset: first.value,
            limit: limit.value,
        }).map((p: any) => ({
            ...p,
            _underlag: p.underlag_display?.namn ?? "",
            tableInfo: {
                isNewlyCreated: !!newlyCreated.value.get(p.id),
                isNewlyUpdated: !!newlyUpdated.value.get(p.id),
            },
        }))
    );

    const filterSchema = generateSchema({
        addToAll: { ignoreEmptyData: true },
        schema: [
            { component: "KText", name: "namn", label: "Namn" },
            { component: "UnderlagAutocomplete", name: "underlag", label: "Underlag" },
        ],
    });

    const columns = [
        { field: "namn", header: "Namn" },
        { field: "_underlag", header: "Underlag" },
    ];

    const tableActions = [
        {
            title: "Redigera plats",
            show: () => true,
            callback: ({ data }: { data: any }) => openForm(data),
        },
    ];

    const fetchList = async () => {
        isLoading.value = true;
        try {
            const { subsetName: fetchedSubset } = await store.dispatch("skreg/platser/fetchPlatser", {
                page: page.value,
                limit: limit.value,
                filterParams: filterParams.value,
            });
            subsetName.value = fetchedSubset;
        } catch {
            openDialog("Kunde inte hämta platser.", "warning");
        } finally {
            isLoading.value = false;
        }
    };

    const onFilter = ({ formData }: { formData: any }) => {
        filterParams.value = formData;
        page.value = 1;
        fetchList();
    };

    const onPage = ({ page: newPage, rows: newRows }: { page: number; rows: number }) => {
        page.value = newPage;
        limit.value = newRows;
        fetchList();
    };

    const openForm = (item = undefined) => {
        store.dispatch("tabs/openTab", {
            component: FormPlats,
            parameters: item,
        });
    };

    onMounted(async () => {
        await store.dispatch("skreg/platser/addSubscriber");
        await fetchList();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("skreg/platser/removeSubscriber");
    });
</script>

<style lang="scss" scoped></style>
